/* @jsx h */
import { defineComponent, h } from "vue";
import { cn, useLocalization } from "@lemmonhq/uikit";
import { ReservationSchedule } from "@lemmonhq/types";
import { daysOfWeek as DAYS } from "@lemmonhq/orders";

export type ReservationDaySelectProps = {
  value: ReservationSchedule;
  onChange: (schedule: ReservationSchedule) => void;
};

export const ReservationDaySelect = defineComponent<ReservationDaySelectProps>({
  name: "ReservationDaySelect",
  props: ["value", "onChange"],
  setup(props) {
    const { t } = useLocalization();

    const addTimeRange = (day: string) => {
      const currentRanges = props.value[day] || [];
      const newSchedule = {
        ...props.value,
        [day]: [
          ...currentRanges,
          {
            start_time: "09:00",
            end_time: "17:00",
          },
        ],
      };
      props.onChange(newSchedule);
    };

    const removeTimeRange = (day: string, index: number) => {
      const newRanges = [...(props.value[day] || [])];
      newRanges.splice(index, 1);
      const newSchedule = {
        ...props.value,
        [day]: newRanges,
      };
      if (newRanges.length === 0) {
        delete newSchedule[day];
      }
      props.onChange(newSchedule);
    };

    const updateTimeRange = (
      day: string,
      index: number,
      field:
        | "start_time"
        | "end_time"
        | "max_occupancy"
        | "pre_payment"
        | "auto_accept_seats",
      value: string | number | undefined,
    ) => {
      const newRanges = [...(props.value[day] || [])];
      newRanges[index] = {
        ...newRanges[index],
        [field]: value,
      };
      props.onChange({
        ...props.value,
        [day]: newRanges,
      });
    };

    return () => (
      <div class="space-y-1">
        {DAYS.map((day) => (
          <div key={day} class="card border bg-base-200  p-0">
            <div class="card-body px-4 py-2">
              <div class="flex items-center justify-between p-0">
                <h4
                  class={cn(
                    "card-title text-sm uppercase font-normal",
                    props.value[day] && "text-accent font-semibold",
                  )}
                >
                  {day}
                </h4>
                <button
                  type="button"
                  class="btn btn-xs btn-secondary text-center"
                  onClick={() => addTimeRange(day)}
                >
                  <span class="text-sm">+</span>
                </button>
              </div>

              <div class="space-y-1">
                {(props.value[day] || []).map((range, index) => (
                  <div
                    key={index}
                    class="grid grid-cols-2 gap-3 lg:grid-cols-[repeat(5,minmax(0,1fr))_auto] lg:items-end"
                  >
                    <label class="flex min-w-0 flex-col gap-1.5">
                      <span class="flex items-end text-xs text-base-content/60">
                        {t("reservations.settings.start_time")}
                      </span>
                      <input
                        type="time"
                        value={range.start_time}
                        onChange={(e) =>
                          updateTimeRange(
                            day,
                            index,
                            "start_time",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="input input-bordered input-sm w-full focus:input-primary"
                      />
                    </label>

                    <label class="flex min-w-0 flex-col gap-1.5">
                      <span class="flex items-end text-xs text-base-content/60">
                        {t("reservations.settings.end_time")}
                      </span>
                      <input
                        type="time"
                        value={range.end_time}
                        onChange={(e) =>
                          updateTimeRange(
                            day,
                            index,
                            "end_time",
                            (e.target as HTMLInputElement).value,
                          )
                        }
                        class="input input-bordered input-sm w-full focus:input-primary"
                      />
                    </label>

                    <label class="flex min-w-0 flex-col gap-1.5">
                      <span class="flex items-end text-xs text-base-content/60">
                        {t("reservations.settings.max_occupancy")}
                      </span>
                      <input
                        type="number"
                        value={range.max_occupancy ?? ""}
                        placeholder=".."
                        min="0"
                        onInput={(e) => {
                          const val = (e.target as HTMLInputElement).value;
                          if (parseInt(val)) {
                            updateTimeRange(
                              day,
                              index,
                              "max_occupancy",
                              parseInt(val),
                            );
                          } else {
                            updateTimeRange(
                              day,
                              index,
                              "max_occupancy",
                              undefined,
                            );
                          }
                        }}
                        class="input input-bordered input-sm w-full focus:input-primary"
                      />
                    </label>

                    <label class="flex min-w-0 flex-col gap-1.5">
                      <span class="flex items-end text-xs text-base-content/60">
                        {t("reservations.settings.pre_payment")}
                      </span>
                      <input
                        type="number"
                        value={range.pre_payment ?? ""}
                        placeholder=".."
                        min="0"
                        step="0.01"
                        onInput={(e) => {
                          const val = (e.target as HTMLInputElement).value;
                          if (parseFloat(val)) {
                            updateTimeRange(
                              day,
                              index,
                              "pre_payment",
                              parseFloat(val),
                            );
                          } else {
                            updateTimeRange(
                              day,
                              index,
                              "pre_payment",
                              undefined,
                            );
                          }
                        }}
                        class="input input-bordered input-sm w-full focus:input-primary"
                      />
                    </label>

                    <label class="flex min-w-0 flex-col gap-1.5">
                      <span class="flex items-end text-xs text-base-content/60">
                        {t("reservations.settings.auto_accept_seats")}
                      </span>
                      <input
                        type="number"
                        value={range.auto_accept_seats ?? ""}
                        placeholder=".."
                        min="0"
                        onInput={(e) => {
                          const val = (e.target as HTMLInputElement).value;
                          if (parseInt(val)) {
                            updateTimeRange(
                              day,
                              index,
                              "auto_accept_seats",
                              parseInt(val),
                            );
                          } else {
                            updateTimeRange(
                              day,
                              index,
                              "auto_accept_seats",
                              undefined,
                            );
                          }
                        }}
                        class="input input-bordered input-sm w-full focus:input-primary"
                      />
                    </label>

                    <button
                      type="button"
                      class="btn btn-sm btn-seconary self-end justify-self-end"
                      onClick={() => removeTimeRange(day, index)}
                    >
                      <span class="material-symbols-outlined text-error">
                        delete
                      </span>
                    </button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  },
});
